import axios from "axios";

const SERVER = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Superseded — ConnectShopifyButton.jsx no longer has a manual-domain-entry dialog to
// call this from (Shopify App Store requirement 2.3.1); its backend endpoint is also
// commented out. Commented out, not deleted.
// /**
//  * GET /api/integrations/shopify/login — asks the backend to build the Shopify
//  * authorization URL. The frontend never constructs OAuth URLs itself.
//  * @returns {Promise<string>} authorizationUrl
//  */
// export async function fetchShopifyAuthorizationUrl(workspaceId, shopDomain, token) {
//   const response = await axios.get(`${SERVER}/api/integrations/shopify/login`, {
//     params: { workspaceId, shop: shopDomain },
//     headers: { Authorization: `Bearer ${token}` },
//   });
//   return response.data?.data?.authorizationUrl;
// }

/**
 * GET /api/integrations/shopify/pricing-check?shop=... — the actual 2.3.3 fix. Called
 * by ShopifyConnecting.jsx the moment it loads, right after OAuth — tells the page
 * where to go next: straight to /signup (Partner API not configured yet), to Shopify's
 * hosted pricing page (no active subscription for this shop), or to /welcome
 * (subscription already confirmed). Public endpoint, no token needed.
 * @returns {Promise<{action: "signup"|"pricing"|"welcome", pricingUrl?: string}>}
 */
export async function checkShopifyPricingStatus(shop) {
  const response = await axios.get(`${SERVER}/api/integrations/shopify/pricing-check`, {
    params: { shop },
  });
  return response.data?.data;
}

/**
 * GET /api/workspaces — not Shopify-specific, reused as-is for the workspace-picker
 * step on Welcome.jsx (already-logged-in case) and Login.jsx (post-login case). Kept
 * here rather than a new service file since both current call sites are Shopify-connect
 * flows; see workspaceSlice.js's fetchWorkspaces thunk for the Redux-backed equivalent
 * used by the rest of the app.
 * @returns {Promise<Array>} workspaces
 */
export async function fetchMyWorkspaces(token) {
  const response = await axios.get(`${SERVER}/api/workspaces`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data?.workspaces || [];
}

/**
 * POST /api/integrations/shopify/claim-pending — claims a pending Shopify connection
 * (OAuth already ran, anonymously-looking, for a shop that isn't yet tied to a
 * ClusterDen workspace) into the now-authenticated merchant's own account/workspace.
 * Called from Login.jsx right after a successful login/OTP verification when `?shop=`
 * is present. Never touches billing — see claimPendingConnectionForExistingAccount's
 * own comment (shopifyOAuthService.js).
 */
export async function claimPendingShopifyConnection(shop, workspaceId, token) {
  const response = await axios.post(
    `${SERVER}/api/integrations/shopify/claim-pending`,
    { shop, workspaceId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

/**
 * GET /api/integrations/shopify/status — retrieves the connected workspace's
 * Shopify store status.
 * @returns {Promise<object>} status data
 */
export async function fetchShopifyStatus(workspaceId, token) {
  const response = await axios.get(`${SERVER}/api/integrations/shopify/status`, {
    params: { workspaceId },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data?.data;
}

/**
 * POST /api/integrations/shopify/disconnect — disconnects the workspace's
 * Shopify store.
 */
export async function disconnectShopify(workspaceId, token) {
  const response = await axios.post(
    `${SERVER}/api/integrations/shopify/disconnect`,
    { workspaceId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}

/**
 * GET /api/integrations/shopify/orders — order history for a contact matched
 * to a Shopify customer, for the Inbox sidebar. Returns [] (never throws) for
 * a contact that isn't a Shopify customer or has no connected store.
 * @returns {Promise<Array>} orders
 */
export async function fetchShopifyOrders(contactId, token) {
  const response = await axios.get(`${SERVER}/api/integrations/shopify/orders`, {
    params: { contactId },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data?.data?.orders || [];
}
